
import {defineComponent} from "vue";
import {CodeDescriptionRestDto, TitleMetaDataRestDto} from "@/api/pharma-cms-content-block/models";
import PageLinkPicker from "@/components/UI/PageLinkPicker.vue";
import {PageLinkPayload} from "@/models/payload/PageLinkPayload";
import {PageLinkTypeEnum} from "@/models/enum/PageLinkTypeEnum";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {LinkTargetEnum} from "@/models/enum/LinkTargetEnum";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {ImageTargetEnum} from "@/api/enums/image-target-enum";
import {TextWithImageContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextWithImageContentRestDtoModel";
import {TextWithImageContentBlockLayoutEnum} from "@/api/enums/text-with-image-content-block-layout-enum";
import {ButtonTargetEnum} from "@/api/enums/button-target-enum";
import {ContentBlockTypesEnum} from "@/helpers/functions/enum";
import {ButtonRestDtoModel} from "@/models/api/pharma-cms-content-block/ButtonRestDtoModel";
import {ImageRestDtoModel} from "@/models/api/pharma-cms-content-block/ImageRestDtoModel";
import Subtitle from "@/components/UI/Subtitle.vue";
import {CopyCpAbstractPayload} from "@/models/payload/CopyCpAbstractPayload";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import InputText from "@/components/UI/InputText.vue";
import InputTextEditor from "@/components/UI/InputTextEditor.vue";
import AdvancedPanelSwitcher from "@/components/UI/AdvancedPanelSwitcher.vue";
import {TitleMetaDataRestDtoModel} from "@/models/api/pharma-cms-content-block/TitleMetaDataRestDtoModel";
import TitleMetaInputComponent from "@/components/UI/TitleMetaInputComponent.vue";
import AdvancedPanel from "@/components/UI/AdvancedPanel.vue";
import InputImage from "@/components/UI/InputImage.vue";
import ImageContainerDimensionsDetail
  from "@/components/layouts/content-pages/wysiwyg/misc/ImageContainerDimensionsDetail.vue";
import ImagePositioningDetail from "@/components/layouts/content-pages/wysiwyg/misc/ImagePositioningDetail.vue";
import ExtraMetadataSection from "@/components/layouts/content-pages/wysiwyg/misc/ExtraMetadataSection.vue";
import ImageOverlaysDetail from "@/components/layouts/content-pages/wysiwyg/misc/ImageOverlaysDetail.vue";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";

export default defineComponent({
  name: "CbTextWithImage",
  extends: CbStructureMixin,
  components: {
    InputImage,
    ImageContainerDimensionsDetail,
    ImagePositioningDetail,
    ExtraMetadataSection,
    ImageOverlaysDetail,
    AdvancedPanelSwitcher,
    AdvancedPanel, TitleMetaInputComponent,
    InputTextEditor,
    InputText, InputCheckboxBoolean, InputSelect, BaseSpinner, Subtitle, PageLinkPicker
  },
  props: {
    contentToolbar: {
      type: String,
      required: false,
    },
    editorOptions: {
      type: Object,
      required: false,
    },
    isAdvancedOptionsAvailable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data: () => ({
    contentBlock: TextWithImageContentRestDtoModel.createWithDefaults(),
    pageLinkButtonPayload: PageLinkPayload.createWithDefaults(),
    pageLinkImagePayload: PageLinkPayload.createWithDefaults(),
    docType: 'PAGE_IMG' as string,
    useButton: false as boolean,
    removedButton: ButtonRestDtoModel.createWithDefaults(),
    isAdvancedTitleMeta: false,
    // Bumped whenever the content block is (re)loaded or saved, so the metadata
    // group components remount and re-capture their reset baseline.
    metadataRevision: 0 as number,
    enumButtonLayoutCode: [] as CodeDescriptionRestDto[],
    enumBorderTypeCode: [] as CodeDescriptionRestDto[],
    enumBorderWidthCode: [] as CodeDescriptionRestDto[],
    enumColumnSizeCode: [] as CodeDescriptionRestDto[],
    enumHorizontalImageShift: [] as CodeDescriptionRestDto[],
    enumLightboxLayoutCode: [] as CodeDescriptionRestDto[],
    enumObjectHorizontalPosition: [] as CodeDescriptionRestDto[],
    enumLayoutCode: [] as CodeDescriptionRestDto[],
    enumTitleSizeCode: [] as CodeDescriptionRestDto[],
    enumVerticalImageShift: [] as CodeDescriptionRestDto[],
  }),
  mounted() {
    this.initialize();
  },
  watch: {
    contentBlock() {
      this.buildPageLinkPayload();

      if (this.contentBlock.button) {
        this.useButton = true;
        this.removedButton = this.contentBlock.button;
      } else {
        this.useButton = false;
        this.contentBlock.button = undefined;
        this.removedButton = ButtonRestDtoModel.createWithDefaults();
      }
      if (!this.contentBlock.image) {
        this.contentBlock.image = ImageRestDtoModel.createWithDefaults()
      }

      this.metadataRevision++;

      this.onCopyCpAbstractChanged();
    },
    useButton(newVal) {
      if (newVal === true) {
        this.contentBlock.button = this.removedButton;

        this.buildPageLinkButtonPayload();
      } else {
        if (this.contentBlock.button !== undefined) {
          this.removedButton = this.contentBlock.button;
        }
        this.contentBlock.button = undefined
      }
    },
  },
  computed: {
    DpModuleEnum() {
      return DpModuleEnum;
    },
    existingDocumentKey(): string | null {
      return this.contentBlock.image?.image_key?.nl ?? null;
    },
    isArticleHeading(): boolean {
      return this.cbType === ContentBlockTypesEnum.ARTICLE_HEADING;
    },
    isOverview(): boolean {
      return this.cbType === ContentBlockTypesEnum.OVERVIEW_INFO;
    },
    // BR-IMGRATIO-07: the API rejects an image column width on the above-text layout,
    // where the image spans the full width instead of sitting in its own column.
    showImageColWidth(): boolean {
      return !!this.contentBlock.layout_code
        && this.contentBlock.layout_code !== TextWithImageContentBlockLayoutEnum.IMAGE_ABOVE_TEXT;
    },
    metadataSectionId(): string {
      return `cb_text_with_image_${this.cbId ?? 'new'}`;
    },
    activeMetadataLabels(): string[] {
      const labels = [] as string[];

      if (this.contentBlock.image?.container_dimensions) {
        labels.push(this.$t('cms.container'));
      }
      if (this.contentBlock.image?.positioning) {
        labels.push(this.$t('cms.positioning'));
      }
      if (this.contentBlock.image?.overlays?.length) {
        labels.push(this.$t('cms.overlays'));
      }

      return labels;
    },
  },
  methods: {
    async afterSubmitData(): Promise<void> {
      // What was just saved becomes the new reset baseline of the metadata groups.
      this.metadataRevision++;
    },
    async find(payload: FindNormalContentBlockPayload): Promise<TextWithImageContentRestDtoModel> {
      return await NormalContentBlockRestService.getInstance().findTextWithImage(payload.type, payload.id);
    },
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      return await NormalContentBlockRestService.getInstance().createTextWithImage(payload.cb as TextWithImageContentRestDtoModel, payload.type);
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      await NormalContentBlockRestService.getInstance().saveTextWithImage(payload.cb as TextWithImageContentRestDtoModel, payload.type, payload.id);
    },
    async initialize(): Promise<void> {
      [
        this.enumButtonLayoutCode,
        this.enumBorderTypeCode,
        this.enumBorderWidthCode,
        this.enumColumnSizeCode,
        this.enumHorizontalImageShift,
        this.enumLightboxLayoutCode,
        this.enumObjectHorizontalPosition,
        this.enumLayoutCode,
        this.enumTitleSizeCode,
        this.enumVerticalImageShift
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BUTTON_LAYOUT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BORDER_TYPE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BORDER_WIDTH, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.COLUMN_SIZE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.HORIZONTAL_IMAGE_SHIFT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.LIGHTBOX, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_HORIZONTAL_POSITION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TEXT_WITH_IMAGE_LAYOUT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TITLE_SIZE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.VERTICAL_IMAGE_SHIFT, DpModuleEnum.CMS)
      ]);
    },
    toggleAdvancedTitleMeta(): void {
      if (!this.isAdvancedTitleMeta && (this.contentBlock.title_meta === null || this.contentBlock.title_meta === undefined)) {
        this.contentBlock.title_meta = TitleMetaDataRestDtoModel.createWithDefaults();
      }
      this.isAdvancedTitleMeta = !this.isAdvancedTitleMeta;
    },
    // Switching to the above-text layout drops the column width: keeping it would make
    // the next save fail on BR-IMGRATIO-07 with a value the form no longer shows.
    onLayoutChanged(): void {
      if (!this.showImageColWidth) {
        this.contentBlock.image_col_width_code = null;
      }

      this.onChange();
    },
    onTitleMetaUpdate(payload: TitleMetaDataRestDto): void {
      this.contentBlock.title_meta = payload;
      this.onChange();
    },
    async beforeSubmitData(): Promise<void> {
      if (this.contentBlock !== null && this.contentBlock.title_meta !== null && this.contentBlock.title_meta !== undefined
        && this.contentBlock.title_meta.font_size_in_rem === null
        && this.contentBlock.title_meta.font_weight_code === null
        && this.contentBlock.title_meta.text_horizontal_position_code === null
        && this.contentBlock.title_meta.text_color_code === null
        && this.contentBlock.title_meta.text_color === null) {
        this.contentBlock.title_meta = null;
      }
    },
    afterReloadContent() {
      if (this.contentBlock.title_meta) {
        this.isAdvancedTitleMeta = true;
      }
      if (this.contentBlock.button) {
        this.useButton = true;
        this.removedButton = this.contentBlock.button;
      }
      if (!this.contentBlock.image) {
        this.contentBlock.image = ImageRestDtoModel.createWithDefaults()
      }

    },
    getDefaultCbModel(): TextWithImageContentRestDtoModel {
      return TextWithImageContentRestDtoModel.createWithDefaults();
    },
    buildPageLinkPayload(): void {
      this.buildPageLinkButtonPayload();
      this.buildPageLinkImagePayload();
    },
    buildPageLinkButtonPayload(): void {
      const externalUrlButton = this.contentBlock.button?.button_url?.nl ?? null;
      const pageIdButton = this.contentBlock.button?.button_page_id?.nl ?? null;
      const targetButton = this.contentBlock.button?.button_target ?? LinkTargetEnum.SELF;

      this.pageLinkButtonPayload = new PageLinkPayload(externalUrlButton, pageIdButton, targetButton.toString() as LinkTargetEnum);
    },
    buildPageLinkImagePayload(): void {
      const externalUrlImage = this.contentBlock.image?.image_url?.nl ?? null;
      const pageIdImage = this.contentBlock.image?.image_page_id?.nl ?? null;
      const targetImage = this.contentBlock.image?.image_target ?? LinkTargetEnum.SELF;

      this.pageLinkImagePayload = new PageLinkPayload(externalUrlImage, pageIdImage, targetImage.toString() as LinkTargetEnum);
    },
    onCopyCpAbstractChanged(): void {
      if (!this.isOverview) {
        const title = this.contentBlock.title?.nl ?? null;
        const content = this.contentBlock.content?.nl ?? null;
        const image_key = this.contentBlock?.image?.image_key?.nl ?? null;
        this.copyCpAbstractPayload = new CopyCpAbstractPayload(content, image_key, title);

        this.$emit('abstractContentChanged', this.copyCpAbstractPayload);
      }
    },
    onPageLinkPickerButtonChanged(payload: PageLinkPayload) {
      if (this.contentBlock.button) {
        if (payload.type === PageLinkTypeEnum.NONE) {
          this.contentBlock.button.button_url = MultilingualRestDtoModel.createWithDefaults();
          this.contentBlock.button.button_page_id = undefined;
          this.contentBlock.button.button_target = ButtonTargetEnum.SELF;
        } else if (payload.type === PageLinkTypeEnum.URL) {
          this.contentBlock.button.button_url = new MultilingualRestDtoModel(null, null, null, payload.external_url);
          this.contentBlock.button.button_page_id = undefined;
          this.contentBlock.button.button_target = payload.target.toString() as ButtonTargetEnum;
        } else if (payload.type === PageLinkTypeEnum.PAGE) {
          this.contentBlock.button.button_url = MultilingualRestDtoModel.createWithDefaults();
          this.contentBlock.button.button_page_id = new MultilingualRestDtoModel(null, null, null, payload.page_id);
          this.contentBlock.button.button_target = payload.target.toString() as ButtonTargetEnum;
        }
      }

      this.onChange();
    },
    onPageLinkPickerImageChanged(payload: PageLinkPayload) {
      if (this.contentBlock.image) {
        if (payload.type === PageLinkTypeEnum.NONE) {
          this.contentBlock.image.image_url = undefined;
          this.contentBlock.image.image_page_id = undefined;
          this.contentBlock.image.image_target = undefined;
        } else if (payload.type === PageLinkTypeEnum.URL) {
          this.contentBlock.image.image_url = new MultilingualRestDtoModel(null, null, null, payload.external_url);
          this.contentBlock.image.image_page_id = undefined;
          this.contentBlock.image.image_target = payload.target.toString() as ImageTargetEnum;
        } else if (payload.type === PageLinkTypeEnum.PAGE) {
          this.contentBlock.image.image_url = undefined;
          this.contentBlock.image.image_page_id = new MultilingualRestDtoModel(null, null, null, payload.page_id);
          this.contentBlock.image.image_target = payload.target.toString() as ImageTargetEnum;
        }
      }

      this.onChange();
    },
    async setImageKey(key: string | null): Promise<void> {
      if (this.contentBlock.image) {
        this.contentBlock.image.image_key = new MultilingualRestDtoModel(null, null, null, key);
      }

      if (key) {
        this.contentBlock.image_key = new MultilingualRestDtoModel(null, null, null, key);
      } else {
        this.contentBlock.image_key = undefined;
      }

      this.onChange();
    },
    pasteCbMetadata(): void {
      // Reassigning contentBlock (instead of mutating it) is what fires the contentBlock watcher.
      const cbClone = TextWithImageContentRestDtoModel.cloneFrom(this.contentBlock);

      cbClone.cloneMetadataFrom(this.getClipboardContentBlock(this.cbType) as TextWithImageContentRestDtoModel);
      this.contentBlock = cbClone;


      this.buildPageLinkPayload();
    },
  }
});
